// Server-side fetchers for the PUBLIC forum surface (forum.insuracentral.com,
// host-routed to /fpublic). Read-only, cached via Next data-cache tags — the
// browser Supabase/axios stack is never used here.

export const API_BASE = ((process.env.NEXT_PUBLIC_FORUM_BACKEND_URL || process.env.NEXT_PUBLIC_BACKEND_URL) || "https://main-fex-platform-service.onrender.com").replace(/\/+$/, "");

export const FORUM_ORIGIN = "https://forum.insuracentral.com";
export const APP_ORIGIN = "https://app.insuracentral.com";
// Licensedproducer public profile for a verified agent. Only the custom
// domain serves the app; the onrender host 404s everything.
export const LP_ORIGIN = (process.env.NEXT_PUBLIC_LICENSEDPRODUCER_URL || "https://licensedproducer.com").replace(/\/+$/, "");
export const lpProfileUrl = (slug: string) => `${LP_ORIGIN}/agents/${encodeURIComponent(slug)}`;
export const SIGNUP_URL = `${APP_ORIGIN}/auth/sign-up?utm_source=forum&utm_medium=referral&utm_campaign=community`;

async function publicGet<T>(path: string, revalidate: number, tags: string[]): Promise<T | null> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/public/forum${path}`, {
      next: { revalidate, tags },
    });
    if (!res.ok) return null;
    const json = await res.json();
    const outer = json?.data;
    if (outer && typeof outer === "object" && "data" in outer && Object.keys(outer).length <= 3) {
      return outer.data as T;
    }
    return (outer ?? json) as T;
  } catch {
    return null;
  }
}

export interface PubAuthor {
  // Public profile handle (/u/<handle>). The public API never sends account ids.
  handle: string | null;
  name: string;
  // Admin-authored content is published as "InsuraCentral" with a Staff mark.
  isStaff?: boolean;
  avatarUrl: string | null;
  karma: number;
  lpSlug?: string | null;
}
export interface PubCategory {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string | null;
  postCount: number;
}
export interface PubPost {
  id: string;
  slug: string;
  title: string;
  bodyPreview: string;
  imageUrl: string | null;
  category: { id: string; slug: string; name: string; icon: string | null };
  author: PubAuthor;
  score: number;
  commentCount: number;
  viewCount: number;
  isPinned: boolean;
  isLocked: boolean;
  isFresh?: boolean;
  createdAt: string;
  lastActivityAt: string;
}
export interface PubPostDetail extends Omit<PubPost, "bodyPreview" | "imageUrl" | "author"> {
  body: string;
  author: PubAuthor | null;
  // Tombstone (mod-removed or self-deleted): body/title replaced, author null.
  isDeleted: boolean;
  images: { url: string; width: number | null; height: number | null }[];
  upvotes: number;
  downvotes: number;
  isEdited: boolean;
}
export interface PubComment {
  id: string;
  postId: string;
  parentCommentId: string | null;
  depth: number;
  body: string;
  isRemoved: boolean;
  author: PubAuthor | null;
  score: number;
  createdAt: string;
}

// "The Board" — 30-day floor numbers + thread of the month.
export interface PubBoard {
  window: "30d" | "all";
  threads: number;
  replies: number;
  wins: number;
  caseHelp: number;
  agents: number;
  featured: {
    slug: string;
    title: string;
    score: number;
    commentCount: number;
    category: { name: string; slug: string };
    author: { name: string; isStaff?: boolean };
    createdAt: string;
  } | null;
}

export const getBoard = () => publicGet<PubBoard>("/board", 300, ["forum-board"]);

export interface PubSearchHit extends PubPost {
  snippet: string;
  matchedIn: "title" | "body" | "replies" | "author" | "category" | "close";
  replyHits: number;
}
export const searchPosts = (q: string, page = 0) =>
  publicGet<{ query: string; posts: PubSearchHit[]; hasMore: boolean; page: number; terms: string[] }>(
    `/search?q=${encodeURIComponent(q)}&page=${page}`,
    120,
    ["forum-search"],
  );

export const getCategories = () =>
  publicGet<PubCategory[]>("/categories", 3600, ["forum-categories"]);

export const getFeed = (opts: { sort?: string; category?: string; cursor?: string | null; limit?: number } = {}) => {
  const params = new URLSearchParams();
  if (opts.sort) params.set("sort", opts.sort);
  if (opts.category) params.set("category", opts.category);
  if (opts.cursor) params.set("cursor", opts.cursor);
  params.set("limit", String(opts.limit || 25));
  return publicGet<{ posts: PubPost[]; pinned: PubPost[]; hasMore: boolean; nextCursor: string | null }>(
    `/posts?${params.toString()}`,
    300,
    ["forum-feed", ...(opts.category ? [`forum-cat-${opts.category}`] : [])],
  );
};

export const getPost = (idOrSlug: string) =>
  publicGet<PubPostDetail>(`/posts/${encodeURIComponent(idOrSlug)}`, 600, [`forum-post-${idOrSlug}`]);

export const getComments = (postId: string) =>
  publicGet<{ comments: PubComment[]; totalCount: number; capped: boolean }>(
    `/posts/${postId}/comments?sort=top`,
    600,
    [`forum-comments-${postId}`],
  );

export const getUserProfile = (handle: string) =>
  publicGet<any>(`/users/${encodeURIComponent(handle)}`, 900, [`forum-user-${handle}`]);

// Browser-only view beacon: one counted view per viewer per post per 12h,
// de-duplicated server-side. Server renders never count.
export async function registerView(postId: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/v1/public/forum/posts/${encodeURIComponent(postId)}/view`, { method: "POST", keepalive: true });
  } catch {
    // best effort
  }
}

export const getSitemapEntries = (page: number) =>
  publicGet<{ entries: { slug: string; lastmod: string }[] }>(`/sitemap?page=${page}`, 3600, ["forum-sitemap"]);

export function timeAgo(iso: string): string {
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

export function fmt(n: number): string {
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}
